import { FunctionComponent, useCallback, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { makeStyles, mergeClasses } from '@griffel/react'
import { tokens } from '@fluentui/tokens'
import {
  Menu, MenuTrigger, MenuButton, MenuPopover, MenuList, MenuItem,
  Tooltip,
  ToggleButton,
} from '@fluentui/react-components'
import {
  TextBulletListLtrRegular,
  GridRegular,
  TableRegular,
  SquareMultipleRegular,
} from '@fluentui/react-icons'
import { useGenericListSettings } from '@knowmax/genericlist-core'
import { LANGUAGE_LAYOUTCARD, LANGUAGE_LAYOUTCOMPACT, LANGUAGE_LAYOUTGRID, LANGUAGE_LAYOUTLIST } from '../../types/language'
import { TListLayout } from './List'

export type TLayoutOption = {
  layout: TListLayout
  /** Translation key used with onTranslate, or a fallback label when not translated. */
  labelKey: string
  /** Fallback label shown when no translator is configured. */
  defaultLabel: string
  icon: JSX.Element
}

/** Default layout options. Labels are resolved via the onTranslate mechanism at render time. */
export const ALL_LAYOUT_OPTIONS: TLayoutOption[] = [
  { layout: 'list',    labelKey: LANGUAGE_LAYOUTLIST,    defaultLabel: 'List',    icon: <TextBulletListLtrRegular /> },
  { layout: 'grid',    labelKey: LANGUAGE_LAYOUTGRID,    defaultLabel: 'Grid',    icon: <GridRegular /> },
  { layout: 'compact', labelKey: LANGUAGE_LAYOUTCOMPACT, defaultLabel: 'Compact', icon: <TableRegular /> },
  { layout: 'card',    labelKey: LANGUAGE_LAYOUTCARD,    defaultLabel: 'Cards',   icon: <SquareMultipleRegular /> },
]

export interface ILayoutSelectorProps {
  /** Currently active layout. */
  value: TListLayout
  /** Callback fired when the user selects a different layout. */
  onChange: (layout: TListLayout) => void
  /**
   * Subset of layouts to offer. Defaults to all four layouts.
   * When exactly two layouts are provided a two-icon toggle bar is shown;
   * for three or more a menu button is shown.
   */
  availableLayouts?: TListLayout[]
}

const useClasses = makeStyles({
  toggleGroup: {
    display: 'inline-flex',
    flexDirection: 'row',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  toggleButton: {
    borderRadius: '0',
    border: 'none',
    minWidth: '32px',
  },
  toggleButtonFirst: {
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
  },
  toggleButtonLast: {
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
  },
})

/** Renders a layout selector for a list. Shows a two-icon toggle for two options, or a menu button for three or more. */
export const LayoutSelector: FunctionComponent<ILayoutSelectorProps> = observer(({ value, onChange, availableLayouts }) => {
  const classes = useClasses()
  const settings = useGenericListSettings()

  const options = useMemo(() => {
    const base = availableLayouts
      ? ALL_LAYOUT_OPTIONS.filter(o => availableLayouts.includes(o.layout))
      : ALL_LAYOUT_OPTIONS
    return base.map(o => ({ ...o, label: settings.onTranslate(o.labelKey) ?? o.defaultLabel }))
  }, [availableLayouts, settings.onTranslate])

  const handleSelect = useCallback((layout: TListLayout) => {
    if (layout !== value) {
      onChange(layout)
    }
  }, [value, onChange])

  if (options.length === 0) return null

  // Two options: render a compact toggle button bar
  if (options.length === 2) {
    return (
      <div className={classes.toggleGroup} role="toolbar" aria-label="Layout">
        {options.map((option, i) => (
          <Tooltip key={option.layout} content={option.label as string} relationship="label" withArrow>
            <ToggleButton
              appearance="subtle"
              icon={option.icon}
              checked={option.layout === value}
              onClick={() => handleSelect(option.layout)}
              className={mergeClasses(
                classes.toggleButton,
                i === 0 && classes.toggleButtonFirst,
                i === options.length - 1 && classes.toggleButtonLast,
              )}
            />
          </Tooltip>
        ))}
      </div>
    )
  }

  // Three or more options: render a menu button
  const current = options.find(o => o.layout === value) ?? options[0]

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Tooltip content={`Layout: ${current.label as string}`} relationship="label" withArrow>
          <MenuButton appearance="subtle" icon={current.icon} aria-label={`Layout: ${current.label}`} />
        </Tooltip>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {options.map(option => (
            <MenuItem
              key={option.layout}
              icon={option.icon}
              onClick={() => handleSelect(option.layout)}
              aria-pressed={option.layout === value}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  )
})
